import {
  printAddress,
} from '@kathondvla/sri-client/address-utils';
import {
  getNow,
  isAfter,
  isBefore,
  isBeforeOrEqual,
  getPreviousDay,
  printDate,
} from '@kathondvla/sri-client/date-utils';
import { OU_TYPE_CONFIG } from './typeConfig';

export const getPeriodInfo = (relation) => {
  if (isBeforeOrEqual(relation.endDate, relation.startDate)) {
    return 'verwijderd';
  }
  return relation.endDate
    ? `van ${printDate(relation.startDate)} tot ${printDate(relation.endDate)}`
    : `${isAfter(relation.startDate, getNow()) ? 'vanaf' : 'sinds'} ${printDate(
        relation.startDate
      )}`;
}

export const isCurrentOne = (relation, ou) => {
  const referenceEndDateDate = getPreviousDay(ou.endDate) || getNow();
  const referenceStartDate =  isAfter(ou.startDate, getNow()) ? ou.startDate : getNow();
  return isAfter(relation.endDate, referenceEndDateDate) && isBeforeOrEqual(relation.startDate, referenceStartDate);
}

export const transformRelation = (relation, identifiers, ouEndDate, pendingRelation) => {
  const otherOuHref = relation.from.$$expanded ? relation.from.href : relation.to.href;
  const otherOu = relation.from.$$expanded || relation.to.$$expanded;
  const relVm = {
    resource: relation,
    href: otherOuHref,
    ouType: otherOu.type,
    institutionNumber:
      otherOu.code ||
      identifiers.find(
        (id) => id.type === 'INSTITUTION_NUMBER' && id.organisationalUnit.href === otherOuHref
      )?.value,
    name: otherOu.$$displayName,
    periodInfo: getPeriodInfo(relation),
    isDeleted: isBeforeOrEqual(relation.endDate, relation.startDate),
    isActive:
      (ouEndDate && relation.endDate === ouEndDate) ||
      (isAfter(relation.endDate, getNow()) && isBeforeOrEqual(relation.startDate, getNow())),
    isFuture: isAfter(relation.startDate, getNow()),
    isPending: !!pendingRelation,
    startDate: printDate(relation.startDate),
  };
  relVm.display = `${relVm.institutionNumber ? `${relVm.institutionNumber} - ` : ''}${relVm.name}`;
  relVm.path = `/#!/admin?${relVm.institutionNumber ? `nr=${relVm.institutionNumber}` : `href=${otherOuHref}`}`;
  return relVm;
};

export const transformOu = (
  { ouResource, contactDetails, relations, locations, identifiers, legalOrganisationalForm },
  pendingResources,
  referenceDate
) => {
  const ouTypeConfig = OU_TYPE_CONFIG[ouResource.type];
  const allRelations = {
    ...Object.fromEntries(relations.map((rel) => [rel.$$meta.permalink, rel])),
    ...pendingResources.relations,
  };
  const allLocations = [
    ...locations.filter(
      (loc) =>
        !Object.keys(pendingResources.locations).some((href) => loc.$$meta.permalink === href)
    ),
    ...Object.values(pendingResources.locations),
  ];
  allLocations.sort((a, b) =>
    !b.discimusSerialNumber || a.discimusSerialNumber < b.discimusSerialNumber ? -1 : 1
  );
  const transformedRelations = Object.values(allRelations).map((rel) =>
    transformRelation(rel, identifiers, referenceDate, pendingResources.relations[rel.$$meta.permalink])
  );
  transformedRelations.sort((a, b) => {
    if (a.isActive || a.resource.endDate === b.resource.endDate) {
      return a.name < b.name ? -1 : 1;
    }
    return isBefore(a.resource.endDate, b.resource.endDate) ? -1 : 1;
  });
  const relationsVm = {};
  if (ouTypeConfig.components.relations) {
    ouTypeConfig.components.relations.forEach((relConfig) => {
      const ouTypes = relConfig.ouTypes;
      relationsVm[relConfig.key] = {
        currentOu: relConfig.canOnlyHaveOne && transformedRelations.find(
          (rel) => rel.resource.type === relConfig.type && rel.resource[relConfig.thisOu].href === ouResource.$$meta.permalink && ouTypes.has(rel.ouType) && rel.isActive
        ),
        currentOus: !relConfig.canOnlyHaveOne && transformedRelations.filter(
          (rel) => rel.resource.type === relConfig.type && rel.resource[relConfig.thisOu].href === ouResource.$$meta.permalink && ouTypes.has(rel.ouType) && rel.isActive
        ),
        historicalOus: transformedRelations.filter(
          (rel) => rel.resource.type === relConfig.type && rel.resource[relConfig.thisOu].href === ouResource.$$meta.permalink && ouTypes.has(rel.ouType) && !rel.isActive && !rel.isFuture
        ),
        futureOus: transformedRelations.filter(
          (rel) => rel.resource.type === relConfig.type && rel.resource[relConfig.thisOu].href === ouResource.$$meta.permalink && ouTypes.has(rel.ouType) && rel.isFuture
        )
      };
    });
  }
  return {
    href: ouResource.$$meta.permalink,
    resource: ouResource,
    name: ouResource.names.find(
      (nameObj) => nameObj.type === 'OFFICIAL' && isCurrentOne(nameObj, ouResource)
    ).value,
    nameHistory: ouResource.names
      .filter((nameObj) => nameObj.type === 'OFFICIAL')
      .map((nameObj) => ({ value: nameObj.value, periodInfo: getPeriodInfo(nameObj), isCurrent: isCurrentOne(nameObj, ouResource), isFuture: isAfter(nameObj.startDate, getNow()) })),
    shortName: ouResource.names.find(
      (nameObj) => nameObj.type === 'SHORT'  && isCurrentOne(nameObj, ouResource)
    )?.value,
    shortNameHistory: ouResource.names
      .filter((nameObj) => nameObj.type === 'SHORT')
      .map((nameObj) => ({ value: nameObj.value, periodInfo: getPeriodInfo(nameObj), isCurrent: isCurrentOne(nameObj, ouResource), isFuture: isAfter(nameObj.startDate, getNow()) })),
    callName: ouResource.names.find(
      (nameObj) => nameObj.type === 'CALL'  && isCurrentOne(nameObj, ouResource)
    )?.value,
    description: ouResource.description,
    startDate: ouResource.startDate,
    endDate: ouResource.endDate,
    institutionNumber: identifiers.find(
      (id) =>
        id.type === 'INSTITUTION_NUMBER' &&
        id.organisationalUnit.href === ouResource.$$meta.permalink
    )?.value,
    companyNumber: identifiers.find(
      (id) =>
        id.type === 'COMPANY_NUMBER' && id.organisationalUnit.href === ouResource.$$meta.permalink
    ),
    vatNumber: identifiers.find(
      (id) =>
        id.type === 'VAT_NUMBER' && id.organisationalUnit.href === ouResource.$$meta.permalink
    ),
    legalOrganisationalForm,
    currentSeat: allLocations.filter((loc) => loc.type === 'SEAT' && isCurrentOne(loc, ouResource))[0],
    seatHistory: allLocations
      .filter((loc) => loc.type === 'SEAT')
      .map((loc) => ({
        asString: printAddress(loc.physicalLocation.$$expanded.address),
        periodInfo: loc.endDate
          ? `van ${printDate(loc.startDate)} tot ${printDate(loc.endDate)}`
          : `${isAfter(loc.startDate, getNow()) ? 'vanaf' : 'sinds'} ${printDate(loc.startDate)}`,
        isFuture: isAfter(loc.startDate, getNow()),
      })),
    locations: allLocations
      .filter((loc) => loc.type === 'VESTIGINGSPLAATS')
      .map((loc) => ({
        resource: loc,
        address: loc.physicalLocation.$$expanded.address,
        discimusSerialNumber: loc.discimusSerialNumber,
        startDate: loc.startDate,
        endDate: loc.endDate,
        asString: printAddress(loc.physicalLocation.$$expanded.address),
        periodInfo: loc.endDate
          ? `van ${printDate(loc.startDate)} tot ${printDate(loc.endDate)}`
          : `${isAfter(loc.startDate, getNow()) ? 'vanaf' : 'sinds'} ${printDate(loc.startDate)}`,
        isActiveOrFuture:
          (referenceDate && loc.endDate === referenceDate) || isAfter(loc.endDate, getNow()),
      })),
    email: contactDetails.find((cd) => cd.type === 'EMAIL'),
    phone: contactDetails.find((cd) => cd.type === 'PHONE'),
    website: contactDetails.reduce(
      (website, cd) =>
        cd.type === 'WEBSITE' && (!website || website.priority > cd.priority) ? cd : website,
      null
    ),
    mail: contactDetails.find((cd) => cd.type === 'MAIL'),
    relations: relationsVm,
  };
};

export const getFormModel = (ou) => ({
  name: ou.name,
  shortName: ou.shortName,
  callName: ou.callName,
  description: ou.description,
  dateOfNameChange: getNow(),
  institutionNumber: ou.institutionNumber,
  companyNumber: ou.companyNumber ? ou.companyNumber.value : null,
  vatNumber: ou.vatNumber ? ou.vatNumber.value : null,
  legalForm: ou.legalOrganisationalForm ? ou.legalOrganisationalForm.legalOrganisationalForm.$$expanded : null,
  startDate: ou.startDate,
  endDate: ou.endDate,
  email: ou.email ? ou.email.value.email : null,
  phone: ou.phone ? ou.phone.value.phone : null,
  website: ou.website ? ou.website.value.website : null,
  mail: ou.mail ? { ...ou.mail.value } : null,
  dateOfAddressChange: !ou.mail ? ou.startDate : getNow(),
});
